<template>
  <Layout>
    <div class="ecu-apply ecu-review memberStep" :class="{ 'is-loading': dataLoading }">
      <section class="ea-flow">
        <header class="ea-flow__head">
          <h1>{{ isReview ? "Review" : "View" }} <span>Application</span></h1>
          <p v-if="isReview">
            Check every section, complete the official-use checklist, then
            record your decision.
          </p>
          <p v-else>A read-only view of the submitted membership application.</p>
        </header>

        <!-- who / what / where it stands + the main action -->
        <div class="er-summary">
          <div class="er-summary__main">
            <span class="er-summary__tile"><AppIcon name="file" :size="22" /></span>
            <div class="er-summary__item">
              <small>Application No.</small>
              <strong>
                <span v-if="dataLoading" class="ecu-skeleton er-summary__skeleton"></span>
                <template v-else>{{ appNumber || "N/A" }}</template>
              </strong>
            </div>
            <div class="er-summary__item">
              <small>Applicant</small>
              <strong>
                <span v-if="dataLoading" class="ecu-skeleton er-summary__skeleton"></span>
                <template v-else>{{ applicantDisplayName || "N/A" }}</template>
              </strong>
            </div>
            <div class="er-summary__item">
              <small>Status</small>
              <span v-if="dataLoading" class="ecu-skeleton er-summary__skeleton" style="width: 90px"></span>
              <StatusChip v-else-if="currentAppStatus" :status="currentAppStatus" />
              <strong v-else>N/A</strong>
            </div>
          </div>

          <div class="er-summary__actions">
            <router-link
              :to="isAdminRole ? `/single_applicant/${app_id}` : { name: 'application list' }"
              class="er-btn er-btn--ghost"
            >
              <AppIcon name="chevron-left" :size="16" :stroke="2.2" />
              {{ isAdminRole ? "Applicant Details" : "Applications" }}
            </router-link>
            <button
              v-if="showOfficial"
              type="button"
              class="er-btn er-btn--primary"
              @click="showModal = true"
            >
              <AppIcon name="edit" :size="16" /> Take Action
            </button>
          </div>
        </div>
        <div v-if="dataLoading" class="er-loadbar" role="status" aria-label="Loading application"></div>

        <template v-if="showRadio">
          <form-wizard
            @on-complete="onComplete"
            ref="myWizard"
            :start-index="startIndex"
            step-size="xs"
            color="#f97316"
            :style="{ '--ea-steps': stepCount }"
            @saveFormData="saveFormData"
            id="pdf_data_form"
          >
            <tab-content class="tab_0" title="Membership Type">
              <ReviewApplicantType />
            </tab-content>
            <tab-content class="tab_1" title="Personal Information">
              <ReviewPersonalInfo />
            </tab-content>
            <tab-content class="tab_2" title="Employment Details">
              <ReviewEmployment />
            </tab-content>
            <tab-content class="tab_3" title="Account Activity">
              <ReviewAccountActivity />
            </tab-content>
            <tab-content class="tab_4" title="Guardian Details" v-if="radioSelected1 === 'minor'">
              <ReviewMinors />
            </tab-content>

            <tab-content class="tab_5" title="PEP Declaration">
              <ReviewPep />
            </tab-content>

            <tab-content class="tab_6" title="FATCA">
              <ReviewFatca />
            </tab-content>

            <tab-content
              class="tab_7"
              title="Account & Fees"
              v-if="userType === 'verification officer'"
            >
              <ReviewAccountFees />
            </tab-content>

            <tab-content class="tab_8" title="Nominee">
              <ReviewNominee />
            </tab-content>

            <tab-content class="tab_9" title="Declaration">
              <ReviewDeclaration />
            </tab-content>

            <tab-content class="tab_10" title="Official Use" v-if="showOfficial === true">
              <ReviewOfficialUse />
            </tab-content>

            <template #custom-buttons-right="{ activeTabIndex }">
              <span class="ea-step-count">
                Step {{ activeTabIndex + 1 }} of {{ stepCount }}
              </span>
            </template>
          </form-wizard>
        </template>
      </section>

      <!-- status / feedback dialog (opened from the summary strip or the official-use step) -->
      <ReviewFeedbackModal v-if="showOfficial" />
    </div>
  </Layout>
</template>

<style>
.residency-toggle {
  display: inline-flex;
  border: 1px solid #f48226;
  border-radius: 20px;
  overflow: hidden;
}

.residency-opt {
  background: #fff;
  color: #f48226;
  border: 0;
  padding: 4px 16px;
  font-size: 14px;
  cursor: pointer;
}

.residency-opt.active {
  background: #f48226;
  color: #fff;
}

.residency-opt:disabled {
  cursor: default;
}

.preloader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.preloader {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 4px solid #f3f3f3;
  border-top-color: #f48226;
  animation: spin 1s infinite linear;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
<script>
import storageService from "@/services/storage.service";
import {
  FUNDING_SOURCE_CHOICES,
  INCOME_SOURCE_CHOICES,
  PHONE_COUNTRY_CODE_CHOICES,
  DOCUMENT_ISSUANCE_COUNTRIES,
} from "@/constants";
import Layout from "../../layouts/main";
import AppIcon from "@/components/shared/AppIcon.vue";
import StatusChip from "@/components/shared/StatusChip.vue";
import "@/assets/css/ecu-apply.css";
import "@/assets/css/ecu-review.css";
import { FormWizard, TabContent } from "vue3-form-wizard";
import "vue3-form-wizard/dist/style.css";
import {
  ReviewApplicantType,
  ReviewPersonalInfo,
  ReviewEmployment,
  ReviewAccountActivity,
  ReviewMinors,
  ReviewPep,
  ReviewFatca,
  ReviewAccountFees,
  ReviewNominee,
  ReviewDeclaration,
  ReviewOfficialUse,
  ReviewFeedbackModal,
} from "@/components/forms/review";
import { provideFormContext } from "@/components/forms/formBridge";
import axios from "axios";
import statusOptionsMixin from "@/mixins/statusOptions";
import useVuelidate from "@vuelidate/core";
import base_domain from "../../myvars";
import staffReviewShared from "@/mixins/staffReviewShared";
import staffReviewValidations from "@/mixins/staffReviewValidations";

export default {
  mixins: [staffReviewShared, statusOptionsMixin],
  // Step components (components/forms/review) reach this view through formBridge.
  provide: provideFormContext,

  setup() {
    return { v$: useVuelidate() };
  },
  mounted() {
    const buttons = document.querySelectorAll(".wizard-btn");
    buttons.forEach((button) => {
      button.addEventListener("click", this.handleNext);
    });
    this.$refs.myWizard.activateAll();

    this.chkStaff();
  },
  beforeUnmount() {
    const buttons = document.querySelectorAll(".wizard-btn");
    buttons.forEach((button) => {
      button.removeEventListener("click", this.handleNext);
    });
  },
  data() {
    return {
      residencyType: null,
      check_type: storageService.getItem("userType"),
      imageUrl: null,
      imageUrl_two: null,
      imageUrl_three: null,
      imageUrl_four: null,
      imageUrl_five: null,
      imageUrl_six: null,
      imageUrl_seven: null,
      imageUrl_eight: null,
      imageUrl_nine: null,
      fileIconUrl: "https://www.flaticon.com/free-icon/file_124837",
      submitting: false,
      appNumber: "",
      dataLoading: true,
      signatureUploading: false,
      signatureError: "",
      incompleteAppStatus: "select",
      incompleteAppComment: "",
      showModal: false,
      selectStatusPro: "select",
      currentAppStatus: "",
      feedbackError: "",
      reviewerName: "",
      reviewDate: "",
      reviewApprovedDate: "",
      imageSrcStamp: "",
      imageSrcSig: "",
      optionsList: [],
      itemsss: [0],
      startIndex: 0,
      app_id: "",
      isLoading: false,
      showOfficial: false,
      radioSelected1: "",
      checkFeildInput1: "",
      checkFeildInput2: "",
      riskInput: "",
      app_URL: process.env.APP_URL,
      showValue: true,
      fatcaFeild: "",
      checkFeildInput: [],
      radioSelected: "",
      nationalIssuance: "",
      fundedoption: FUNDING_SOURCE_CHOICES,
      Incomeoption: INCOME_SOURCE_CHOICES,
      showRadio: true,
      preview: [],
      image: [],
      driverIssuance: "",
      passportIssuance: "",
      birthIssuance: "",
      countrynameIssuanceOpt: PHONE_COUNTRY_CODE_CHOICES,
      sectionOptions: [
        { value: "section_a1", label: "Personal Information" },
        { value: "section_b2", label: "Employment Information" },
        { value: "section_c3", label: "Account Activity" },
        { value: "section_d4", label: "Minors Only" },
        { value: "section_e5", label: "Politically Exposed Persons" },
        { value: "section_f6", label: "Foreign Account Tax Compliance ACT" },
        { value: "section_h8", label: "Appointment of Nominee" },
        { value: "section_i9", label: "Members Declaration" },
      ],
      nationalIssuanceOpt: DOCUMENT_ISSUANCE_COUNTRIES,
      form: {
        personal: {
          branchName: "",
          title: "",
          surname: "",
          firstName: "",
          middleName: "",
          address: "",
          address_line: "",
          address_line_two: "",
          city_name: "",
          state_name: "",
          zip_code: "",
          country_name: "",
          dob: "",
          gender: "",
          numOfDependents: "",
          placeOfdob: "",
          nationality: "",
          maritalStatus: "",
          nationalIdNum: "",
          nidIssueDate: "",
          nidExpiry: "",
          nationalIssuance: "",
          naitonal_identi: "",
          drivingIdNum: "",
          didIssueDate: "",
          didExpiry: "",
          driverIssuance: "",
          passportNum: "",
          passIssueDate: "",
          passExpiry: "",
          passportIssuance: "",
          birthIdNum: "",
          birthIssuance: "",
          homePhoneNum: "",
          mobileNum: "",
          email: "",
          nidDoc: [],
          didDoc: [],
          passDoc: [],
          birthDoc: [],
          employStatus: "",
          frequencyStatus: "",
          averageIncm: "",
          user_occupation: "",
          employer: "",
          user_employer: "",
          user_w_phone_no: "",
          user_w_phone_nocode: "",
          user_phone_no: "",
          user_isstudent: "",
          user_howlong: "",
          accFunded: "",
          accountActi: "",
          accountActiDesc: "",
          numTrans: "",
          volTrans: "",
          parentName: "",
          parentId: "",
          trusteeName: "",
          advisor: "",
          trusteeId: "",
          declaration: "",
          referredRadio: "",
          entrustedRadio: "",
          memberOfRadio: "",
          tobagoRadio: "",
          ssn_no: "",
          citizenRadio: "",
          addressRadio: "",
          incomeUsRadio: "",
          account_one: "",
          shares_amount: "",
          account_two: "",
          special_shares_amount: "",
          account_three: "",
          deposit_ac_amount: "",
          reg_fee_amount: "",
          total_amount: "",
          appointnominee_name1: "",
          appointnominee_email1: "",
          appointnominee_pno1: "",
          appointnominee_pno1code: "",
          appointnominee_rel1: "",
          appointnominee_address1: "",
          appointnominee_dob1: "",
          appointnominee_name2: "",
          appointnominee_email2: "",
          appointnominee_dob2: "",
          appointnominee_pno2: "",
          appointnominee_pno1code2: "",
          appointnominee_rel2: "",
          appointnominee_address2: "",
          hereRadioType: true,
          trusteeRadioo: "",
          relationbenifit3: "",
          appointnominee_name3: "",
          appointnominee_pno1code3: "",
          appointnominee_pno3: "",
          socuceofincome: "",
          areaofdetails: "",
          appointnominee_email3: "",
          appointnominee_dob3: "",
          appointnominee_address3: "",
          hereRadioType_one: "",
          appliname: "",
          checkListOffice1: "",
          checkListOffice2: "",
          checkListOffice3: "",
          checkListOffice4: "",
          checkListOffice5: "",
          checkListOffice6: "",
        },
      },
    };
  },
  computed: {
    isReview() {
      return this.$route && this.$route.name === "review_application";
    },
    // number of wizard steps currently rendered (drives the stepper + "Step x of y")
    stepCount() {
      return (
        8 +
        (this.radioSelected1 === "minor" ? 1 : 0) +
        (this.userType === "verification officer" ? 1 : 0) +
        (this.showOfficial === true ? 1 : 0)
      );
    },
    isAdminRole() {
      return this.check_type === "admin" || this.check_type === "super_user";
    },
    isStaffRole() {
      return this.check_type === "staff" || this.check_type === "verification officer";
    },
    // staff / verification officers must have a signature on file before they can submit feedback
    needsSignature() {
      return this.isStaffRole && !this.imageSrcSig;
    },
    applicantDisplayName() {
      const n = this.form && this.form.personal && this.form.personal.appliname;
      return n && n !== "N/A" ? n : "";
    },
  },
  created() {
    var app = window.location.href;
    const segments = app.split("/").filter(Boolean);
    const lastSeg = segments[segments.length - 1];
    const app_id = lastSeg && !isNaN(lastSeg) ? lastSeg : (segments[segments.length - 2] && !isNaN(segments[segments.length - 2]) ? segments[segments.length - 2] : "");
    if (app_id) {
      this.app_id = app_id;
      this.getIssueImageUrl(app_id);
    }
  },
  validations: staffReviewValidations,
  components: {
    FormWizard,
    TabContent,
    Layout,
    AppIcon,
    StatusChip,
    ReviewFeedbackModal,
    ReviewApplicantType,
    ReviewPersonalInfo,
    ReviewEmployment,
    ReviewAccountActivity,
    ReviewMinors,
    ReviewPep,
    ReviewFatca,
    ReviewAccountFees,
    ReviewNominee,
    ReviewDeclaration,
    ReviewOfficialUse,
  },

  methods: {
    // same destination as before for staff; admins return to the admin list
    onComplete() {
      window.location.href = this.isAdminRole ? "/applicants" : "/application_list";
    },
    // Saves the reviewer's signature on their profile (same endpoints the My Profile page uses).
    uploadSignature(file) {
      this.signatureError = "";
      if (!file) return;
      if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
        this.signatureError = "Please choose a PNG or JPG image.";
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        this.signatureError = "The image is larger than 2 MB.";
        return;
      }
      this.signatureUploading = true;
      const token = storageService.getItem("accessToken");
      const reader = new FileReader();
      reader.onerror = () => {
        this.signatureUploading = false;
        this.signatureError = "Could not read the selected file.";
      };
      reader.onload = () => {
        const dataUrl = reader.result;
        axios
          .post("/applications/single_user_details/", { app_id: token })
          .then((res) => {
            const u = (res.data && res.data.user_email) || {};
            return axios.post("/applications/update_profile/", {
              token: token,
              f_name: u.first_name,
              l_name: u.last_name,
              profile_pic: null,
              signature: dataUrl,
              stamp: null,
            });
          })
          .then(() => {
            this.imageSrcSig = dataUrl;
          })
          .catch((error) => {
            this.signatureError =
              (error.response && error.response.data && error.response.data.error) ||
              "Could not upload the signature. Please try again.";
          })
          .finally(() => {
            this.signatureUploading = false;
          });
      };
      reader.readAsDataURL(file);
    },
    handleNext() {
      // Proceed to the next tab
      this.scrollToTop();
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    //     // Add content to the PDF document

    //     // Save the PDF
    downloadPDF() {
      const targetId = this.app_id || this.id || this.$route?.params?.id;
      if (targetId) {
        this.$router.push({ name: "PDF Application", params: { id: targetId } });
      } else {
        this.$router.push({ name: "PDF Application" });
      }
    },
    getIssueImageUrl(app_id) {
      axios
        .get(`/applications/issue_image/${app_id}/`)
        .then((response) => {
          const imageUrl = response.data.url || null;
          const imageUrl_two = response.data.url_two || null;
          const imageUrl_three = response.data.url_three || null;
          const imageUrl_four = response.data.url_four || null;
          const imageUrl_five = response.data.url_five || null;
          const imageUrl_six = response.data.url_six || null;
          const imageUrl_seven = response.data.url_seven || null;
          const imageUrl_eight = response.data.url_eight || null;
          const imageUrl_nine = response.data.url_nine || null;

          const formatUrl = (u) => {
            if (!u) return null;
            if (u.startsWith("http://") || u.startsWith("https://")) return u;
            const base = (base_domain || "").replace(/\/+$/, "");
            const path = u.startsWith("/") ? u : `/${u}`;
            return `${base}${path}`;
          };

          if (imageUrl !== null) {
            this.imageUrl = formatUrl(imageUrl);
          }
          if (imageUrl_two !== null) {
            this.imageUrl_two = formatUrl(imageUrl_two);
          }
          if (imageUrl_three !== null) {
            this.imageUrl_three = formatUrl(imageUrl_three);
          }
          if (imageUrl_four !== null) {
            this.imageUrl_four = formatUrl(imageUrl_four);
          }
          if (imageUrl_five !== null) {
            this.imageUrl_five = formatUrl(imageUrl_five);
          }
          if (imageUrl_six !== null) {
            this.imageUrl_six = formatUrl(imageUrl_six);
          }
          if (imageUrl_seven !== null) {
            this.imageUrl_seven = formatUrl(imageUrl_seven);
          }
          if (imageUrl_eight !== null) {
            this.imageUrl_eight = formatUrl(imageUrl_eight);
          }
          if (imageUrl_nine !== null && Array.isArray(imageUrl_nine)) {
            this.imageUrl_nine = imageUrl_nine.map(formatUrl);
          }
        })
        .catch((error) => {
          console.error("Error fetching issue image:", error);
        });
    },
    submitFeedback() {
      if (this.needsSignature) {
        this.feedbackError =
          "Please upload your signature before submitting your feedback.";
        return;
      }
      var t = storageService.getItem("accessToken");
      var app = window.location.href;
      const segments = app.split("/").filter(Boolean);
      const lastSeg = segments[segments.length - 1];
      const app_id = (lastSeg && !isNaN(lastSeg)) ? lastSeg : (this.app_id || segments[segments.length - 2] || "");
      if (app_id) {
        this.app_id = app_id;
      }
      if (this.selectStatusPro !== "select") {
        this.feedbackError = "";
        this.submitting = true;
        this.showModal = false;
        axios
          .post(`/applications/submit_feedback/`, {
            token: t,
            app_id: app_id,
            section: this.incompleteAppStatus,
            com: this.incompleteAppComment,
            status: this.selectStatusPro,
          })
          .then(() => {

            this.selectStatusPro = "select";
            this.incompleteAppComment = "";
            this.incompleteAppStatus = "select";
            this.$router.push(
              this.isAdminRole ? "/applicants" : { name: "application list" },
            );
          })
          .catch((error) => {
            this.submitting = false;
            this.feedbackError =
              (error.response && error.response.data && error.response.data.error) ||
              "Could not update the status.";
          });
      }
    },

    chkStaff() {
      this.dataLoading = true;
      var t = storageService.getItem("userType");
      var tok = storageService.getItem("accessToken");
      var toke = "";
      if (
        window.location.href.includes("review_application") ||
        window.location.href.includes("view_application")
      ) {
        toke = tok;
        this.showOfficial = true;
      } else {
        toke = null;
      }
      var app = window.location.href;
      const segments = app.split("/").filter(Boolean);
      const lastSeg = segments[segments.length - 1];
      const app_id = (lastSeg && !isNaN(lastSeg)) ? lastSeg : (this.app_id || segments[segments.length - 2] || "");
      if (app_id) {
        this.app_id = app_id;
      }
      // Create a new Date object with today's date
      var today = new Date();

      // Format the date as YYYY-MM-DD (or any other desired format)
      var formattedDate = today.toISOString().split("T")[0];
      if (
        t === "staff" ||
        t === "verification officer" ||
        t === "admin" ||
        t === "super_user"
      ) {
        axios
          .get(`/applications/single_application_details/${app_id}`, {
            params: { token: toke },
          })
          .then((response) => {
            // Handle the response
            this.reviewerName = response.data.app_reviewer;
            this.currentAppStatus = response.data.app_data.status;
            this.appNumber =
              response.data.app_data.application_no ||
              `ECU-${String(app_id).padStart(6, "0")}`;
            this.reviewDate = formattedDate;
            this.reviewApprovedDate = formattedDate;
            this.imageSrcStamp = response.data.app_reviewer_stamp;
            this.imageSrcSig = response.data.app_reviewer_sign;
            this.residencyType = response.data.app_data.residency_type || null;
            if (response.data.app_data.is_adult === true) {
              this.radioSelected1 = "adult";
            } else {
              this.radioSelected1 = "minor";
            }

            if (response.data.app_data.section_a1) {
              this.form.personal.branchName =
                response.data.app_data.section_a1.branch;
              this.form.personal.title =
                response.data.app_data.section_a1.applicant_title;
              this.form.personal.firstName =
                response.data.app_data.section_a1.first_name;
              this.form.personal.middleName =
                response.data.app_data.section_a1.middle_name;
              this.form.personal.surname =
                response.data.app_data.section_a1.sur_name;
              this.form.personal.address =
                response.data.app_data.section_a1.home_address;
              this.form.personal.address_line =
                response.data.app_data.section_a1.address_line;
              this.form.personal.address_line_two =
                response.data.app_data.section_a1.address_line_two;
              this.form.personal.city_name =
                response.data.app_data.section_a1.city_name;
              this.form.personal.state_name =
                response.data.app_data.section_a1.state_name;
              this.form.personal.zip_code =
                response.data.app_data.section_a1.zip_code;
              this.form.personal.country_name =
                response.data.app_data.section_a1.country_name;
              this.form.personal.dob = response.data.app_data.section_a1.dob;
              this.form.personal.gender =
                response.data.app_data.section_a1.gender;
              this.form.personal.numOfDependents =
                response.data.app_data.section_a1.dependents;
              this.form.personal.placeOfdob =
                response.data.app_data.section_a1.place_of_birth;
              this.form.personal.nationality =
                response.data.app_data.section_a1.nationality;
              this.form.personal.maritalStatus =
                response.data.app_data.section_a1.marital;
              this.form.personal.nationalIdNum =
                response.data.app_data.section_a1.nat_identity.number;
              this.form.personal.nidIssueDate =
                response.data.app_data.section_a1.nat_identity.issue_on;
              this.form.personal.nidExpiry =
                response.data.app_data.section_a1.nat_identity.expire_on;
              this.form.personal.nationalIssuance =
                response.data.app_data.section_a1.nat_identity.isuue_country;
              this.form.personal.drivingIdNum =
                response.data.app_data.section_a1.driver.number;
              this.form.personal.didIssueDate =
                response.data.app_data.section_a1.driver.issue_on;
              this.form.personal.didExpiry =
                response.data.app_data.section_a1.driver.expire_on;
              this.form.personal.driverIssuance =
                response.data.app_data.section_a1.driver.isuue_country;
              this.form.personal.passportNum =
                response.data.app_data.section_a1.passport.number;
              this.form.personal.passIssueDate =
                response.data.app_data.section_a1.passport.issue_on;
              this.form.personal.passExpiry =
                response.data.app_data.section_a1.passport.expire_on;
              this.form.personal.passportIssuance =
                response.data.app_data.section_a1.passport.isuue_country;
              this.form.personal.birthIdNum =
                response.data.app_data.section_a1.birth_cert.number;
              this.form.personal.birthIssuance =
                response.data.app_data.section_a1.birth_cert.issue_on;

              this.form.personal.homePhoneNum =
                response.data.app_data.section_a1.home_contact;
              this.form.personal.homePhoneNumcode =
                response.data.app_data.section_a1.ext_home_contact;

              this.form.personal.mobileNum =
                response.data.app_data.section_a1.mob_contact;
              this.form.personal.mobileNumcode =
                response.data.app_data.section_a1.ext_mob_contact;

              this.form.personal.email =
                response.data.app_data.section_a1.email;
            }
            if (response.data.app_data.section_b2) {
              this.form.personal.employStatus =
                response.data.app_data.section_b2.emp_status;
              this.form.personal.frequencyStatus =
                response.data.app_data.section_b2.pay_freq;
              this.form.personal.user_howlong =
                response.data.app_data.section_b2.emp_time;
              this.form.personal.averageIncm =
                response.data.app_data.section_b2.avg_mnth_income;
              this.form.personal.user_occupation =
                response.data.app_data.section_b2.occupation;
              this.form.personal.employer =
                response.data.app_data.section_b2.employer;
              this.form.personal.user_employer =
                response.data.app_data.section_b2.emp_address;
              this.form.personal.user_w_phone_no =
                response.data.app_data.section_b2.work_contact;

              this.form.personal.user_w_phone_nocode =
                response.data.app_data.section_b2.ext_work_contact;

              this.form.personal.user_phone_no =
                response.data.app_data.section_b2.extra_ext_work;

              this.form.personal.user_isstudent =
                response.data.app_data.section_b2.school;
            }
            if (response.data.app_data.section_c3) {
              this.form.personal.accFunded =
                response.data.app_data.section_c3.fund_source;
              this.form.personal.accountActi =
                response.data.app_data.section_c3.mem_in_others;
              this.form.personal.accountActiDesc =
                response.data.app_data.section_c3.mem_in_others_desc;
              this.form.personal.socuceofincome =
                response.data.app_data.section_c3.socuce_of_income;
              this.form.personal.areaofdetails =
                response.data.app_data.section_c3.area_of_details;
              this.form.personal.numTrans =
                response.data.app_data.section_c3.nos_trx;
              this.form.personal.volTrans =
                response.data.app_data.section_c3.vol_trx;
            }
            if (response.data.app_data.section_d4) {
              this.form.personal.parentName =
                response.data.app_data.section_d4.incare_g.name;
              this.form.personal.parentId =
                response.data.app_data.section_d4.incare_g.identification;
              this.form.personal.trusteeName =
                response.data.app_data.section_d4.incare_t.name;
              this.form.personal.trusteeId =
                response.data.app_data.section_d4.incare_t.identification;
              this.form.personal.advisor = response.data.app_data.section_d4
                .advisor
                ? response.data.app_data.section_d4.advisor
                : "";
              this.form.personal.declaration =
                response.data.app_data.section_d4.decleration;
            }
            if (response.data.app_data.section_e5) {
              this.form.personal.entrustedRadio =
                response.data.app_data.section_e5.prom_entrust;
              this.form.personal.memberOfRadio =
                response.data.app_data.section_e5.immd_famly;
              this.form.personal.referredRadio =
                response.data.app_data.section_e5.close_prsonl;
            }
            if (response.data.app_data.section_f6) {
              this.form.personal.tobagoRadio =
                response.data.app_data.section_f6.other_citizen;
              this.form.personal.citizenRadio =
                response.data.app_data.section_f6.us_citizen;
              this.form.personal.ssn_no =
                response.data.app_data.section_f6.ssn_no;
              this.form.personal.addressRadio =
                response.data.app_data.section_f6.us_address;
              this.form.personal.incomeUsRadio =
                response.data.app_data.section_f6.givin_instruct;
            }
            if (response.data.app_data.section_g7) {
              this.form.personal.account_one =
                response.data.app_data.section_g7.shares_acc;
              this.form.personal.shares_amount =
                response.data.app_data.section_g7.shares_amt;
              this.form.personal.account_two =
                response.data.app_data.section_g7.spshares_ac;
              this.form.personal.special_shares_amount =
                response.data.app_data.section_g7.spshares_amt;
              this.form.personal.account_three =
                response.data.app_data.section_g7.deposit_ac;
              this.form.personal.deposit_ac_amount =
                response.data.app_data.section_g7.deposit_amt;
              this.form.personal.reg_fee_amount =
                response.data.app_data.section_g7.reg_fee;
              this.form.personal.total_amount =
                response.data.app_data.section_g7.total;
            }
            if (response.data?.app_data?.section_h8) {
              const sec_h8 = response.data.app_data.section_h8;
              this.form.personal.appointnominee_name1 =
                sec_h8.nominee_1?.name ?? "N/A";
              this.form.personal.appointnominee_pno1 =
                sec_h8.nominee_1?.contact ?? "N/A";
              this.form.personal.appointnominee_pno1code =
                sec_h8.nominee_1?.ext_contact ?? "N/A";
              this.form.personal.appointnominee_rel1 =
                sec_h8.nominee_1?.relation ?? "N/A";
              this.form.personal.appointnominee_rel_details =
                sec_h8.nominee_1?.other_relative ?? "N/A";

              this.form.personal.appointnominee_address1 =
                sec_h8.nominee_1?.address ?? "N/A";
              this.form.personal.appointnominee_email1 =
                sec_h8.nominee_1?.email_address ?? "N/A";
              this.form.personal.appointnominee_dob1 =
                sec_h8.nominee_1?.applicant_dob ?? "N/A";

              this.form.personal.appointnominee_name2 =
                response.data?.app_data?.section_h8?.nominee_2?.name ?? "N/A";
              this.form.personal.appointnominee_pno2 =
                response.data?.app_data?.section_h8?.nominee_2?.contact ??
                "N/A";

              this.form.personal.appointnominee_pno1code2 =
                response.data?.app_data?.section_h8?.nominee_2?.ext_contact ??
                "N/A";

              this.form.personal.appointnominee_rel2 =
                response.data?.app_data?.section_h8?.nominee_2?.relation ??
                "N/A";

              this.form.personal.appointnominee_rel2_details =
                response.data?.app_data?.section_h8?.nominee_2
                  ?.other_relative ?? "N/A";

              this.form.personal.appointnominee_address2 =
                response.data?.app_data?.section_h8?.nominee_2?.address ??
                "N/A";
              this.form.personal.appointnominee_email2 =
                response.data?.app_data?.section_h8?.nominee_2?.email_address ??
                "N/A";
              this.form.personal.appointnominee_dob2 =
                response.data?.app_data?.section_h8?.nominee_2?.applicant_dob ??
                "N/A";

              this.form.personal.trusteeRadioo =
                response.data?.app_data?.section_h8?.nominee_minor?.caretaker
                  ?.type ?? "N/A";
              this.form.personal.appointnominee_name3 =
                response.data?.app_data?.section_h8?.nominee_minor?.name ??
                "N/A";

              this.form.personal.appointnominee_pno1code3 =
                response.data?.app_data?.section_h8?.nominee_minor
                  ?.ext_contact ?? "N/A";

              this.form.personal.appointnominee_pno3 =
                response.data?.app_data?.section_h8?.nominee_minor?.contact ??
                "N/A";
              this.form.personal.relationbenifit3 =
                response.data?.app_data?.section_h8?.nominee_minor?.relation ??
                "N/A";
              this.form.personal.relationbenifit3_relation =
                response.data?.app_data?.section_h8?.nominee_minor
                  ?.other_relative ?? "N/A";

              this.form.personal.appointnominee_address3 =
                response.data?.app_data?.section_h8?.nominee_minor?.address ??
                "N/A";
              this.form.personal.appointnominee_email3 =
                response.data?.app_data?.section_h8?.nominee_minor
                  ?.email_address ?? "N/A";
              this.form.personal.appointnominee_dob3 =
                response.data?.app_data?.section_h8?.nominee_minor
                  ?.applicant_dob ?? "N/A";
            }

            const a1 = response.data?.app_data?.section_a1;
            this.form.personal.appliname = a1 ? `${a1.first_name || ""} ${a1.sur_name || ""}`.trim() : "N/A";
            if (response.data?.app_data?.section_i9) {
              this.form.personal.hereRadioType =
                response.data.app_data.section_i9.decleration;
              this.form.personal.nameofparent =
                response.data?.app_data?.section_d4?.incare_g?.name ?? "N/A";
              this.form.personal.nameoftrustee =
                response.data?.app_data?.section_d4?.incare_t?.name ?? "N/A";
            }
          })
          .catch((error) => {
            // Handle any errors
            console.error("Error sending email:", error);
          })
          .finally(() => {
            this.dataLoading = false;
          });
      } else {
        this.dataLoading = false;
        return true;
      }
    },
    viewApplication(app_id) {
      const targetId = app_id || this.app_id;
      if (!targetId) return;
      const t = storageService.getItem("accessToken");
      axios
        .post("applications/view_application/", {
          headers: {
            token: `Bearer ${t}`,
            app_id: targetId,
          },
          application_id: targetId,
        })
        .then(() => {
          // Handle the API response
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
};
</script>